import { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'app/state'
import { CartoApiError, RequestStatus } from 'features/common'
import { OAuthConsentUrl, OAuthState, setOAuthBillingProjectsError } from 'features/connections'
import { useQueryClient } from 'react-query'
import { getOAuthConstentUrl, getOAuthState } from 'features/connections/api/connections'
import settings from 'settings'

const POPUP_HEIGHT = 700
const POPUP_WIDTH = 600
const POPUP_TOP = window.outerHeight / 2 + window.screenY - POPUP_HEIGHT / 2
const POPUP_LEFT = window.outerWidth / 2 + window.screenX - POPUP_WIDTH / 2

export const oauthPopupName = 'oauth-popup'
export const oauthPopupProps = `toolbar=no, menubar=no, width=${POPUP_WIDTH}, height=${POPUP_HEIGHT}, top=${POPUP_TOP}, left=${POPUP_LEFT}`

function useOAuth() {
  const queryClient = useQueryClient()
  const [oauthState, setOAuthState] = useState<OAuthState | null>()
  const [oauthError, setOAuthError] = useState<CartoApiError | null>()
  const [broadcastChannel, setBroadcastChannel] = useState<BroadcastChannel | null>(null)

  const dispatch = useAppDispatch()

  const oauthProjectsStatus = useAppSelector((state) => state.connections.oauthBillingProjectsStatus)
  const areProjectsFetched = oauthProjectsStatus === RequestStatus.Fulfilled

  const isLoading = areProjectsFetched

  const resetErrors = useCallback(() => {
    dispatch(setOAuthBillingProjectsError(null))
  }, [dispatch])

  const resetStatus = useCallback(() => {
    setOAuthState(null)
    setOAuthError(null)
  }, [])

  const handleOAuthSuccess = useCallback(
    async (
      event: MessageEvent<{ status: string; params: string }>,
      provider: string,
      onSuccess: (value: OAuthState) => void,
      onReject: (reason: unknown) => void
    ) => {
      const message = event.data
      if (message.status === 'success') {
        try {
          const oauthState = await consumeOAuthState({ provider, params: message.params })
          setOAuthState(oauthState)
          onSuccess(oauthState)
        } catch (e) {
          onReject(e)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const openSignInWindow = useCallback(
    (
      { url, provider }: OAuthConsentUrl,
      onSuccess: (value: OAuthState) => void,
      onReject: (reason: unknown) => void
    ) => {
      let windowObjectReference: WindowProxy | null = null

      if (!windowObjectReference || (windowObjectReference as WindowProxy).closed) {
        windowObjectReference = window.open(url, oauthPopupName)

        const checkPopupStatus = () => {
          if (!windowObjectReference || windowObjectReference.closed) {
            window.clearInterval(pollTimer)
            window.removeEventListener('message', handleMessage)
            resetStatus()
            return
          }
        }

        const handleMessage = async (event: MessageEvent<{ status: string; params: string }>) => {
          const message = event.data
          if (message.status === 'success') {
            handleOAuthSuccess(event, provider, onSuccess, onReject)
            window.clearInterval(pollTimer)
            window.removeEventListener('message', handleMessage)
            windowObjectReference?.close()
          }
        }

        window.addEventListener('message', handleMessage)
        const pollTimer = window.setInterval(checkPopupStatus, 500)
      } else {
        ;(windowObjectReference as WindowProxy).focus()
      }

      return windowObjectReference
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const openSignInWindowForNativeApp = useCallback(
    (
      { url, provider }: OAuthConsentUrl,
      onSuccess: (value: OAuthState) => void,
      onReject: (reason: unknown) => void
    ) => {
      resetStatus()
      broadcastChannel?.close()

      const channel = new BroadcastChannel('oauth_channel')
      setBroadcastChannel(channel)

      window.open(url, oauthPopupName)

      const handleSuccess = async (event: MessageEvent<{ status: string; params: string }>) => {
        const message = event.data
        if (message.status === 'success') {
          handleOAuthSuccess(event, provider, onSuccess, onReject)
          broadcastChannel?.close()
        }
      }
      channel.onmessage = handleSuccess
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [broadcastChannel]
  )

  const getConsentUrl = useCallback(
    async (params) =>
      queryClient.fetchQuery({
        queryKey: ['consent_url'],
        queryFn: () => getOAuthConstentUrl(params)
      }),
    [queryClient]
  )

  const consumeOAuthState = useCallback(
    async (params) =>
      queryClient.fetchQuery({
        queryKey: 'consume_state',
        queryFn: () => getOAuthState(params)
      }),
    [queryClient]
  )

  const startOAuth = async (params) => {
    window.open('', oauthPopupName, oauthPopupProps)
    const consent = await getConsentUrl(params)
    try {
      return await new Promise<OAuthState>((resolve, reject) => {
        if (settings.isSnowflakeNativeApp) {
          openSignInWindowForNativeApp(consent, resolve, reject)
        } else {
          openSignInWindow(consent, resolve, reject)
        }
      })
    } catch (e) {
      setOAuthError(e as CartoApiError)
    }
  }

  return {
    oauthState,
    areProjectsFetched,
    resetOAuthStatus: resetStatus,
    isOAuthLoading: isLoading,
    startOAuth,
    resetErrors,
    oauthError
  }
}

export default useOAuth
